import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import Can from '../../auth/Can';
import FieldEditor, { CollapseAllButtons, MoveButtons, OPTION_TYPES, addTopLevelField, useCollapseAll } from '../../components/fields/FieldEditor';
import { listNationalities, createNationality, updateNationality, deleteNationality } from '../../api/masters';
import { listDocumentTypes } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';

function NationalityFormModal({ open, onClose, nationality }) {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes, enabled: open });
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [fields, setFields] = useState([]);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState('');
  const [collapseSignal, setCollapseAll] = useCollapseAll();

  useEffect(() => {
    setName(nationality?.name || '');
    setIsActive(nationality?.is_active ?? true);
    setFields(
      (nationality?.fields || []).map((f) => ({
        ...f,
        options: f.options || [],
        validation: { required: false, date_rule: null, format: null, ...(f.validation || {}) },
        depends_on: f.depends_on ?? null,
      }))
    );
    setDocs((nationality?.documents || []).map((d) => ({ required: true, verify: true, ...d })));
    setError('');
  }, [nationality]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: name.trim(), is_active: isActive, fields, documents: docs };
      return nationality ? updateNationality(nationality.id, payload) : createNationality(payload);
    },
    onSuccess: () => {
      pushToast({ type: 'success', message: nationality ? 'Nationality updated.' : 'Nationality created.' });
      queryClient.invalidateQueries({ queryKey: ['nationalities'] });
      onClose();
    },
    onError: (err) =>
      setError(
        err.response?.data?.errors?.name?.[0] ||
          err.response?.data?.errors?.fields?.[0] ||
          err.response?.data?.message ||
          'Could not save nationality.'
      ),
  });

  const addField = () => setFields((prev) => addTopLevelField(prev));

  const docTypeName = (code) => typesQuery.data?.find((t) => t.code === code)?.name || code;
  const availableDocTypes = (typesQuery.data || []).filter(
    (t) => t.is_active !== false && !docs.some((d) => d.code === t.code)
  );
  const addDoc = (code) => {
    if (!code || docs.some((d) => d.code === code)) return;
    setDocs((prev) => [...prev, { code, required: true, verify: true }]);
  };
  const removeDoc = (code) => setDocs((prev) => prev.filter((d) => d.code !== code));
  const setDocOption = (code, patch) => setDocs((prev) => prev.map((d) => (d.code === code ? { ...d, ...patch } : d)));

  const handleSave = () => {
    setError('');
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (fields.some((f) => !f.label.trim())) {
      setError('Every field needs a label.');
      return;
    }
    const badOptions = fields.find((f) => OPTION_TYPES.includes(f.type) && f.options.filter((o) => o.trim()).length < 2);
    if (badOptions) {
      setError(`"${badOptions.label}" needs at least 2 answer options.`);
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={nationality ? `Edit: ${nationality.name}` : 'New nationality'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active (offered when registering a candidate or lead)
        </label>

        <div className="border-t border-primary pt-5">
          <div className="mb-1.5 flex items-center justify-between">
            <div>
              <p className="block text-sm font-medium text-gray-700">Fields</p>
              <p className="text-xs text-gray-500">Shown on the registration form when this nationality is chosen.</p>
            </div>
            <div className="flex gap-3">
              <CollapseAllButtons onSet={setCollapseAll} />
              <button type="button" onClick={addField} className="text-xs text-primary hover:underline">
                + Add field
              </button>
            </div>
          </div>
          <div className="space-y-4">
            {fields
              .filter((f) => !f.depends_on)
              .map((f) => (
                <div key={f.id} className="flex items-start gap-1.5">
                  <MoveButtons fields={fields} field={f} onFieldsChange={setFields} />
                  <div className="flex-1">
                    <FieldEditor field={f} fields={fields} onFieldsChange={setFields} showBasicDetailsOption={false} collapseSignal={collapseSignal} />
                  </div>
                </div>
              ))}
            {fields.length === 0 && <p className="text-xs text-gray-400">No fields yet.</p>}
          </div>
        </div>

        <div className="border-t border-primary pt-5">
          <div className="mb-1.5 flex items-center justify-between">
            <div>
              <p className="block text-sm font-medium text-gray-700">Documents</p>
              <p className="text-xs text-gray-500">Requested on the registration form when this nationality is chosen.</p>
            </div>
            <Select className="w-52" value="" onChange={(e) => addDoc(e.target.value)} disabled={!availableDocTypes.length}>
              <option value="">+ Add document…</option>
              {availableDocTypes.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1.5">
            {docs.map((entry) => (
              <div key={entry.code} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                <span className="text-sm text-gray-700">{docTypeName(entry.code)}</span>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500">
                    <input
                      type="checkbox"
                      className="accent-primary"
                      checked={entry.required}
                      onChange={(e) => setDocOption(entry.code, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  {entry.required && (
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={entry.verify}
                        onChange={(e) => setDocOption(entry.code, { verify: e.target.checked })}
                      />
                      Must be verified
                    </label>
                  )}
                  <button type="button" onClick={() => removeDoc(entry.code)} className="text-xs text-gray-400 hover:text-red-600">
                    ✕
                  </button>
                </div>
              </div>
            ))}
            {docs.length === 0 && <p className="text-xs text-gray-400">No documents yet.</p>}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

export default function NationalityMasters() {
  const queryClient = useQueryClient();
  const [formTarget, setFormTarget] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const nationalitiesQuery = useQuery({
    queryKey: ['nationalities', { include_inactive: true }],
    queryFn: () => listNationalities({ include_inactive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNationality,
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Nationality deleted.' });
      queryClient.invalidateQueries({ queryKey: ['nationalities'] });
      setDeleteTarget(null);
    },
    onError: (err) => {
      pushToast({
        type: 'error',
        message: err.response?.data?.errors?.name?.[0] || err.response?.data?.message || 'Could not delete nationality.',
      });
      setDeleteTarget(null);
    },
  });

  const columns = [
    { key: 'name', label: 'Name' },
    {
      key: 'fields',
      label: 'Fields',
      render: (row) => (row.fields?.length ? <Badge tone="gray">{row.fields.length}</Badge> : <span className="text-gray-400">—</span>),
    },
    {
      key: 'documents',
      label: 'Documents',
      render: (row) =>
        row.documents?.length ? <Badge tone="gray">{row.documents.length}</Badge> : <span className="text-gray-400">—</span>,
    },
    {
      key: 'is_active',
      label: 'Status',
      render: (row) => <Badge tone={row.is_active ? 'green' : 'slate'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-3 text-sm">
          <Can perm="masters.manage">
            <button
              onClick={() => {
                setFormTarget(row);
                setFormOpen(true);
              }}
              className="text-primary hover:underline"
            >
              Edit
            </button>
            <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline">
              Delete
            </button>
          </Can>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Nationalities"
        description="Manage the nationality options offered when registering a candidate or lead, and the extra fields each one collects."
        actions={
          <Can perm="masters.manage">
            <Button
              onClick={() => {
                setFormTarget(null);
                setFormOpen(true);
              }}
            >
              New Nationality
            </Button>
          </Can>
        }
      />

      <Table
        columns={columns}
        data={nationalitiesQuery.data || []}
        isLoading={nationalitiesQuery.isLoading}
        emptyMessage="No nationalities yet."
      />

      {formOpen && <NationalityFormModal open={formOpen} onClose={() => setFormOpen(false)} nationality={formTarget} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget.id)}
        title="Delete nationality"
        message={`Delete "${deleteTarget?.name}"? This is only possible if no candidate or lead references it.`}
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
